import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Search, Car, Wrench, ShieldCheck, FileText, Gauge, Trash2, Camera } from "lucide-react";
import logger from "@/lib/logger";

const VEHICLE_TYPES = ["2 Wheeler", "Hydra", "Truck", "Tempo", "Forklifter"];
const OWNER_TYPES = ["Personal", "Company Owned"];
const FUEL_TYPES = ["Diesel", "Petrol", "CNG"];

const RECORD_META = {
  service: { label: "Service", icon: Wrench, color: "text-emerald-700" },
  insurance: { label: "Insurance", icon: ShieldCheck, color: "text-blue-700" },
  fitness: { label: "Fitness / Passing", icon: FileText, color: "text-amber-700" },
  puc: { label: "PUC", icon: Gauge, color: "text-violet-700" },
};

export default function Vehicles() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "manager";

  const [vehicles, setVehicles] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [recordTab, setRecordTab] = useState("service");
  const [recordForm, setRecordForm] = useState({});
  const [uploading, setUploading] = useState(false);

  const fetchVehicles = useCallback(async () => {
    try {
      const { data } = await api.get("/vehicles/master", { params: { search: search || undefined } });
      setVehicles(data.entries || []);
    } catch (err) { logger.error("Fetch vehicles failed", err); }
  }, [search]);

  const fetchReminders = useCallback(async () => {
    try {
      const { data } = await api.get("/vehicles/reminders", { params: { lead_days: 30 } });
      setReminders(data.reminders || []);
    } catch (err) { logger.error("Fetch reminders failed", err); }
  }, []);

  useEffect(() => { fetchVehicles(); fetchReminders(); }, [fetchVehicles, fetchReminders]);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleCreate = async () => {
    if (!form.vehicle_no?.trim()) { toast.error("Vehicle No. is required"); return; }
    if (!form.vehicle_type) { toast.error("Vehicle Type is required"); return; }
    setSaving(true);
    try {
      await api.post("/vehicles/master", form);
      toast.success("Vehicle added");
      setShowForm(false); setForm({}); fetchVehicles();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  const handleDeleteVehicle = async (id) => {
    if (!window.confirm("Delete this vehicle? Service/insurance history will be hidden but audit-trailed.")) return;
    try {
      await api.delete(`/vehicles/master/${id}`);
      toast.success("Vehicle deleted");
      setDetail(null); fetchVehicles();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const openDetail = async (vehicle) => {
    try {
      const { data } = await api.get(`/vehicles/master/${vehicle.id}`);
      setDetail(data);
      setRecordTab("service");
      setRecordForm({});
    } catch (err) { toast.error("Could not load vehicle"); }
  };

  const handleUploadInvoice = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const { data } = await api.post("/upload/photo", { photo: reader.result });
        setRecordForm((p) => ({ ...p, invoice_url: data.url }));
        toast.success("Invoice uploaded");
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch {
      toast.error("Upload failed"); setUploading(false);
    }
  };

  const handleAddRecord = async () => {
    if (!detail?.id) return;
    const rt = recordTab;
    // Simple validations per record type
    if (rt === "service" && !recordForm.service_date) { toast.error("Service date required"); return; }
    if (rt === "service" && !recordForm.next_due_date) { toast.error("Next service due date required"); return; }
    if ((rt === "insurance" || rt === "fitness" || rt === "puc") && !recordForm.expiry_date) { toast.error("Expiry date required"); return; }
    try {
      await api.post(`/vehicles/master/${detail.id}/${rt}`, recordForm);
      toast.success(`${RECORD_META[rt].label} record added`);
      const { data } = await api.get(`/vehicles/master/${detail.id}`);
      setDetail(data); setRecordForm({}); fetchVehicles(); fetchReminders();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleDeleteRecord = async (rt, recordId) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      await api.delete(`/vehicles/master/${detail.id}/${rt}/${recordId}`);
      toast.success("Record removed");
      const { data } = await api.get(`/vehicles/master/${detail.id}`);
      setDetail(data); fetchVehicles(); fetchReminders();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const records = detail ? (detail[`${recordTab}_records`] || []) : [];

  return (
    <div className="space-y-5" data-testid="vehicles-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "Chivo" }}>
            <Car size={24} className="text-[#1B3C87]" /> Vehicles Master
          </h1>
          <p className="text-xs sm:text-sm text-gray-500">Company vehicle register · service · insurance · fitness · PUC</p>
        </div>
        <Button onClick={() => { setForm({}); setShowForm(true); }} className="bg-[#1B3C87] hover:bg-[#2650A8] h-10 sm:h-11 text-xs sm:text-sm w-full sm:w-auto" data-testid="new-vehicle-btn">
          <Plus size={18} className="mr-1" /> New Vehicle
        </Button>
      </div>

      {reminders.length > 0 && (
        <Card className="p-4 border-amber-200 bg-amber-50/40" data-testid="reminders-card">
          <h3 className="font-bold text-sm tracking-wider uppercase text-amber-800 mb-2">Upcoming & Overdue Reminders ({reminders.length})</h3>
          <div className="space-y-1.5 max-h-40 overflow-y-auto">
            {reminders.slice(0, 10).map((r) => (
              <div key={r.id} className="flex items-start justify-between text-xs border-b border-amber-100 pb-1">
                <div>
                  <span className="font-bold text-gray-800">{r.vehicle_no}</span>
                  <span className="text-gray-500 ml-2">{r.make} {r.model}</span>
                </div>
                <div className="flex gap-1 flex-wrap justify-end">
                  {r.alerts.map((a) => (
                    <span key={a.type} className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${a.status === "expired" ? "bg-red-100 text-red-700" : a.status === "due_soon" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-700"}`}>
                      {a.label}: {a.status === "missing" ? "NOT SET" : a.due_date}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="flex items-center gap-2">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search vehicle, make, driver..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9 w-full sm:w-72" data-testid="vehicle-search" />
        </div>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow className="bg-[#1B3C87] hover:bg-[#1B3C87]">
              {["Vehicle No.", "Type", "Make/Model", "Owner", "Driver", "Next Service", "Insurance", "Fitness", "PUC"].map((h) => (
                <TableHead key={h} className="text-white text-xs uppercase tracking-wider font-bold">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {vehicles.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-8 text-gray-400">No vehicles registered yet</TableCell></TableRow>}
            {vehicles.map((v) => (
              <TableRow key={v.id} className="hover:bg-blue-50/50 cursor-pointer" onClick={() => openDetail(v)} data-testid={`vehicle-row-${v.vehicle_no}`}>
                <TableCell className="font-bold font-mono">{v.vehicle_no}</TableCell>
                <TableCell className="text-sm">{v.vehicle_type || "—"}</TableCell>
                <TableCell className="text-sm">{v.make || "—"} {v.model || ""}</TableCell>
                <TableCell>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${v.owner_type === "Personal" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>
                    {v.owner_type || "Company Owned"}
                  </span>
                </TableCell>
                <TableCell className="text-sm">{v.assigned_driver || v.employee_name || "—"}</TableCell>
                <TableCell className={`text-xs ${v.is_service_overdue ? "text-red-600 font-bold" : ""}`}>{v.next_service_due || "—"}</TableCell>
                <TableCell className={`text-xs ${v.is_insurance_expired ? "text-red-600 font-bold" : ""}`}>{v.next_insurance_due || "—"}</TableCell>
                <TableCell className={`text-xs ${v.is_fitness_expired ? "text-red-600 font-bold" : ""}`}>{v.next_fitness_due || "—"}</TableCell>
                <TableCell className={`text-xs ${v.is_puc_expired ? "text-red-600 font-bold" : ""}`}>{v.next_puc_due || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="p-3 border-t text-xs text-gray-500">{vehicles.length} total vehicles</div>
      </Card>

      {/* New Vehicle Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: "Chivo" }}>New Vehicle</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Vehicle No. *</Label>
              <Input value={form.vehicle_no || ""} onChange={(e) => setField("vehicle_no", e.target.value.toUpperCase())} className="h-11" placeholder="e.g. PB08AB1234" data-testid="vm-field-vehicle-no" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Vehicle Type *</Label>
              <Select value={form.vehicle_type || ""} onValueChange={(v) => setField("vehicle_type", v)}>
                <SelectTrigger className="h-11" data-testid="vm-field-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {VEHICLE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Owner Type *</Label>
              <Select value={form.owner_type || "Company Owned"} onValueChange={(v) => setField("owner_type", v)}>
                <SelectTrigger className="h-11" data-testid="vm-field-owner"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {OWNER_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Fuel Type</Label>
              <Select value={form.fuel_type || "Diesel"} onValueChange={(v) => setField("fuel_type", v)}>
                <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FUEL_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Make</Label>
              <Input value={form.make || ""} onChange={(e) => setField("make", e.target.value)} className="h-11" placeholder="e.g. Tata, Mahindra" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Model</Label>
              <Input value={form.model || ""} onChange={(e) => setField("model", e.target.value)} className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Year</Label>
              <Input type="number" value={form.year || ""} onChange={(e) => setField("year", e.target.value)} className="h-11" placeholder="2020" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Chassis No.</Label>
              <Input value={form.chassis_no || ""} onChange={(e) => setField("chassis_no", e.target.value)} className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Engine No.</Label>
              <Input value={form.engine_no || ""} onChange={(e) => setField("engine_no", e.target.value)} className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Assigned Driver</Label>
              <Input value={form.assigned_driver || ""} onChange={(e) => setField("assigned_driver", e.target.value)} className="h-11" />
            </div>
            {form.owner_type === "Personal" && (
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Employee Name</Label>
                <Input value={form.employee_name || ""} onChange={(e) => setField("employee_name", e.target.value)} className="h-11" />
              </div>
            )}
            <div className="sm:col-span-2 space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Remarks</Label>
              <Input value={form.remarks || ""} onChange={(e) => setField("remarks", e.target.value)} className="h-11" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)} className="h-11">Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-[#1B3C87] hover:bg-[#2650A8] h-11" data-testid="vm-save-btn">
              {saving ? "Saving..." : "Create Vehicle"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vehicle Detail Dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: "Chivo" }} className="flex items-center gap-2">
                  <Car size={20} className="text-[#1B3C87]" /> {detail.vehicle_no}
                  <span className="text-xs font-normal text-gray-400 ml-2">{detail.make} {detail.model} ({detail.year})</span>
                </DialogTitle>
              </DialogHeader>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs bg-slate-50 border rounded p-3">
                <div><p className="text-gray-400 uppercase text-[10px] font-bold">Type</p><p className="font-semibold">{detail.vehicle_type || "—"}</p></div>
                <div><p className="text-gray-400 uppercase text-[10px] font-bold">Owner</p><p className="font-semibold">{detail.owner_type || "—"}</p></div>
                <div><p className="text-gray-400 uppercase text-[10px] font-bold">Fuel</p><p className="font-semibold">{detail.fuel_type || "—"}</p></div>
                <div><p className="text-gray-400 uppercase text-[10px] font-bold">Driver</p><p className="font-semibold">{detail.assigned_driver || detail.employee_name || "—"}</p></div>
                <div><p className="text-gray-400 uppercase text-[10px] font-bold">Chassis</p><p className="font-mono">{detail.chassis_no || "—"}</p></div>
                <div><p className="text-gray-400 uppercase text-[10px] font-bold">Engine</p><p className="font-mono">{detail.engine_no || "—"}</p></div>
              </div>

              <Tabs value={recordTab} onValueChange={(v) => { setRecordTab(v); setRecordForm({}); }} className="mt-2">
                <TabsList className="grid grid-cols-4">
                  {Object.entries(RECORD_META).map(([k, m]) => {
                    const Icon = m.icon;
                    return (
                      <TabsTrigger key={k} value={k} data-testid={`vm-tab-${k}`}>
                        <Icon size={12} className={`mr-1 ${m.color}`} /> {m.label}
                      </TabsTrigger>
                    );
                  })}
                </TabsList>

                {Object.keys(RECORD_META).map((rt) => (
                  <TabsContent key={rt} value={rt} className="space-y-3 mt-3">
                    {/* Add new record form */}
                    {canEdit && (
                      <Card className="p-3 border-blue-200 bg-blue-50/40 space-y-3">
                        <p className="text-[11px] uppercase tracking-wider font-bold text-gray-600">Add {RECORD_META[rt].label} Record</p>
                        {rt === "service" ? (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div><Label className="text-[10px] text-gray-500">Service Date *</Label><Input type="date" value={recordForm.service_date || ""} onChange={(e) => setRecordForm((p) => ({ ...p, service_date: e.target.value }))} className="h-9" data-testid="svc-date" /></div>
                            <div><Label className="text-[10px] text-gray-500">Next Due Date *</Label><Input type="date" value={recordForm.next_due_date || ""} onChange={(e) => setRecordForm((p) => ({ ...p, next_due_date: e.target.value }))} className="h-9" data-testid="svc-next-due" /></div>
                            <div><Label className="text-[10px] text-gray-500">Garage / Service Center</Label><Input value={recordForm.garage || ""} onChange={(e) => setRecordForm((p) => ({ ...p, garage: e.target.value }))} className="h-9" /></div>
                            <div><Label className="text-[10px] text-gray-500">Cost (Rs.)</Label><Input type="number" step="0.01" value={recordForm.cost || ""} onChange={(e) => setRecordForm((p) => ({ ...p, cost: e.target.value }))} className="h-9" /></div>
                            <div><Label className="text-[10px] text-gray-500">Odometer at Service (Km)</Label><Input type="number" value={recordForm.odometer_km || ""} onChange={(e) => setRecordForm((p) => ({ ...p, odometer_km: e.target.value }))} className="h-9" /></div>
                            <div><Label className="text-[10px] text-gray-500">Next Due Odometer (Km)</Label><Input type="number" value={recordForm.next_due_odometer || ""} onChange={(e) => setRecordForm((p) => ({ ...p, next_due_odometer: e.target.value }))} className="h-9" /></div>
                            <div className="sm:col-span-2"><Label className="text-[10px] text-gray-500">Parameters / Work Done</Label><Input value={recordForm.parameters || ""} onChange={(e) => setRecordForm((p) => ({ ...p, parameters: e.target.value }))} className="h-9" placeholder="e.g. Oil change, brake pads, air filter" data-testid="svc-params" /></div>
                            <div className="sm:col-span-2">
                              <Label className="text-[10px] text-gray-500">Service Invoice (PDF/Image)</Label>
                              <div className="flex items-center gap-2 mt-1">
                                <label className="flex items-center gap-1 px-3 py-1.5 text-xs border rounded cursor-pointer hover:bg-slate-50" data-testid="svc-invoice-upload">
                                  <Camera size={12} /> {recordForm.invoice_url ? "Replace" : "Attach"} Invoice
                                  <input type="file" accept="image/*,application/pdf" className="hidden" onChange={handleUploadInvoice} />
                                </label>
                                {recordForm.invoice_url && <a href={recordForm.invoice_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 underline">View</a>}
                                {uploading && <span className="text-[10px] text-amber-600">Uploading...</span>}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div><Label className="text-[10px] text-gray-500">Issue / Start Date</Label><Input type="date" value={recordForm.start_date || ""} onChange={(e) => setRecordForm((p) => ({ ...p, start_date: e.target.value }))} className="h-9" /></div>
                            <div><Label className="text-[10px] text-gray-500">Expiry Date *</Label><Input type="date" value={recordForm.expiry_date || ""} onChange={(e) => setRecordForm((p) => ({ ...p, expiry_date: e.target.value }))} className="h-9" data-testid={`${rt}-expiry`} /></div>
                            <div><Label className="text-[10px] text-gray-500">Provider / Issuing Authority</Label><Input value={recordForm.provider || ""} onChange={(e) => setRecordForm((p) => ({ ...p, provider: e.target.value }))} className="h-9" /></div>
                            <div><Label className="text-[10px] text-gray-500">Policy / Certificate No.</Label><Input value={recordForm.policy_no || ""} onChange={(e) => setRecordForm((p) => ({ ...p, policy_no: e.target.value }))} className="h-9" /></div>
                            {rt === "insurance" && <div><Label className="text-[10px] text-gray-500">Premium (Rs.)</Label><Input type="number" step="0.01" value={recordForm.premium || ""} onChange={(e) => setRecordForm((p) => ({ ...p, premium: e.target.value }))} className="h-9" /></div>}
                            <div className="sm:col-span-2">
                              <Label className="text-[10px] text-gray-500">Certificate / Document (PDF/Image)</Label>
                              <div className="flex items-center gap-2 mt-1">
                                <label className="flex items-center gap-1 px-3 py-1.5 text-xs border rounded cursor-pointer hover:bg-slate-50">
                                  <Camera size={12} /> {recordForm.invoice_url ? "Replace" : "Attach"} Document
                                  <input type="file" accept="image/*,application/pdf" className="hidden" onChange={handleUploadInvoice} />
                                </label>
                                {recordForm.invoice_url && <a href={recordForm.invoice_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 underline">View</a>}
                              </div>
                            </div>
                            <div className="sm:col-span-2"><Label className="text-[10px] text-gray-500">Remarks</Label><Input value={recordForm.remarks || ""} onChange={(e) => setRecordForm((p) => ({ ...p, remarks: e.target.value }))} className="h-9" /></div>
                          </div>
                        )}
                        <div className="flex justify-end">
                          <Button size="sm" onClick={handleAddRecord} className="bg-[#1B3C87] hover:bg-[#2650A8] h-8 text-xs" data-testid={`add-${rt}-btn`}>
                            <Plus size={12} className="mr-1" /> Add {RECORD_META[rt].label}
                          </Button>
                        </div>
                      </Card>
                    )}

                    {/* Existing records */}
                    <div>
                      <p className="text-[11px] uppercase tracking-wider font-bold text-gray-500 mb-1">History ({records.length})</p>
                      {records.length === 0 ? (
                        <p className="text-xs text-gray-400 italic">No records yet.</p>
                      ) : (
                        <div className="space-y-1.5">
                          {records.slice().reverse().map((r) => (
                            <div key={r.record_id} className="border rounded p-2 text-xs flex items-start justify-between">
                              <div className="flex-1 space-y-0.5">
                                {rt === "service" ? (
                                  <>
                                    <p><strong>{r.service_date}</strong> → Next due <strong className="text-amber-700">{r.next_due_date}</strong></p>
                                    <p className="text-gray-500">{r.parameters || "—"} · {r.garage || "—"} · Rs. {r.cost || 0}</p>
                                  </>
                                ) : (
                                  <>
                                    <p><strong>{r.provider || "—"}</strong> — {r.policy_no || "—"}</p>
                                    <p className="text-gray-500">{r.start_date || "—"} to <strong className="text-amber-700">{r.expiry_date}</strong></p>
                                  </>
                                )}
                                {r.invoice_url && <a href={r.invoice_url} target="_blank" rel="noreferrer" className="text-[11px] text-blue-600 underline">View Document</a>}
                              </div>
                              {canEdit && (
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400" onClick={() => handleDeleteRecord(rt, r.record_id)} data-testid={`delete-${rt}-${r.record_id}`}>
                                  <Trash2 size={12} />
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </TabsContent>
                ))}
              </Tabs>

              {canEdit && (
                <DialogFooter className="mt-2 pt-2 border-t">
                  <Button variant="outline" onClick={() => handleDeleteVehicle(detail.id)} className="h-9 text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid="vm-delete-vehicle">
                    <Trash2 size={12} className="mr-1" /> Delete Vehicle
                  </Button>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
